import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  Paper,
  IconButton,
  Rating,
  useTheme,
} from "@mui/material";
import {
  Verified,
  Phone,
  RateReview,
  WhatsApp,
  Instagram,
  Telegram,
  X,
  LinkedIn,
  YouTube,
  Language,
} from "@mui/icons-material";
import { motion } from "framer-motion";

// Map social platforms to icons and colors
const SocialIconMap = {
  instagram: { icon: Instagram, color: "#E1306C" },
  telegram: { icon: Telegram, color: "#2AABEE" },
  whatsapp: { icon: WhatsApp, color: "#25D366" },
  twitter: { icon: X, color: "#FFFFFF" },
  linkedin: { icon: LinkedIn, color: "#0077B5" },
  youtube: { icon: YouTube, color: "#FF0000" },
  website: { icon: Language, color: "#00E5FF" },
};

export default function ShortDetails({ coach, onOpenRating }) {
  const theme = useTheme();

  // Extractors
  const coverImage = coach?.coverImage
    ? import.meta.env.VITE_URL + coach.coverImage
    : "/default-cover.jpg";
  const avatarImage = coach?.images?.[0]
    ? import.meta.env.VITE_URL + coach.images[0]
    : coach?.coverImage
      ? import.meta.env.VITE_URL + coach.coverImage
      : "/default-avatar.jpg";
  const genderLabel =
    coach?.gender === "male"
      ? "آقای"
      : coach?.gender === "female"
        ? "خانم"
        : "";
  const price = coach?.pricing?.[0]?.price?.toLocaleString() || "توافقی";

  return (
    <Box component="aside">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Paper
          component="section"
          sx={{
            borderRadius: 4,
            bgcolor: "background.paper",
            border: `1px solid rgba(255, 255, 255, 0.08)`,
            position: { md: "sticky" },
            top: { md: 24 },
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          }}
        >
          {/* Cover */}
          <Box
            sx={{
              height: { xs: 80, md: 100 },
              width: "100%",
              // backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.2), ${theme.palette.background.paper}), url(${coverImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              position: "relative",
            }}
          >
            {coach?.isDiscount && coach?.discount_text && (
              <Chip
                label={coach.discount_text}
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  background:
                    "linear-gradient(45deg, #FF1744 30%, #FF5252 90%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  boxShadow: "0px 4px 12px rgba(255, 23, 68, 0.4)",
                }}
              />
            )}
          </Box>

          {/* Avatar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -6,
              position: "relative",
              zIndex: 2,
            }}
          >
            <Avatar
              src={avatarImage}
              sx={{
                width: { xs: 90, md: 100 },
                height: { xs: 90, md: 100 },
                border: `4px solid ${theme.palette.background.paper}`,
                boxShadow: `0 0 20px ${theme.palette.primary.main}40`,
              }}
            />
          </Box>

          <Box
            sx={{
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              textAlign: "center",
            }}
          >
            <header>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {/* H1 Tag for SEO Optimization */}
                <Typography
                  component="h1"
                  variant="h6"
                  fontWeight={800}
                  color="text.primary"
                  sx={{ fontSize: "1.1rem" }}
                >
                  {genderLabel} {coach?.fullname || "مربی"}
                </Typography>
                <Verified
                  sx={{ color: theme.palette.secondary.main, fontSize: 16 }}
                />
              </Box>
              <Typography
                variant="body2"
                color="primary.light"
                sx={{ mt: 0.5, fontWeight: 600 }}
              >
                {coach?.mainSpecialty?.name || "مربی ورزشی"}
              </Typography>
            </header>

            {coach?.rating_stats?.average_rating > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "rgba(255, 196, 0, 0.1)",
                  py: 0.5,
                  borderRadius: 2,
                }}
              >
                <Rating
                  value={coach.rating_stats.average_rating}
                  precision={0.1}
                  readOnly
                  size="small"
                />
                <Typography
                  variant="caption"
                  color="warning.main"
                  fontWeight={700}
                >
                  ({coach.rating_stats.total_reviews} نظر)
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                bgcolor: "rgba(92, 107, 192, 0.08)",
                p: 1.5,
                borderRadius: 3,
                border: `1px solid ${theme.palette.primary.main}30`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={0.25}
              >
                شروع تعرفه‌ها از
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                color="primary.light"
              >
                {price}{" "}
                <Typography component="span" variant="caption">
                  تومان
                </Typography>
              </Typography>
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}
            >
              {/* <Button
                variant="contained"
                fullWidth
                onClick={() =>
                  (window.location.href = `tel:${coach?.phone || ""}`)
                }
                sx={{ py: 1, fontSize: "0.85rem", gap: "8px" }}
              >
                <Phone sx={{ fontSize: 18 }} /> تماس مستقیم
              </Button> */}
              <Button
                variant="outlined"
                fullWidth
                onClick={onOpenRating}
                sx={{
                  py: 0.8,
                  fontSize: "0.85rem",
                  borderColor: "rgba(255,255,255,0.2)",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.main,
                  },
                  gap: "8px",
                }}
              >
                <RateReview sx={{ fontSize: 18 }} /> ثبت امتیاز
              </Button>
            </Box>

            {/* Social Media Links */}
            {coach?.socialMedia?.length > 0 && (
              <Box
                sx={{
                  mt: 0.5,
                  p: 2,
                  borderRadius: 3,
                  bgcolor: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* هاله نوری ملایم در پس‌زمینه باکس */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "-50%",
                    left: "-50%",
                    width: "200%",
                    height: "200%",
                    background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 60%)`,
                    pointerEvents: "none",
                  }}
                />

                {/* متون ارتباطی */}
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  color="text.primary"
                  sx={{ mb: 0.5, position: "relative", zIndex: 1 }}
                >
                  ارتباط با مربی
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{
                    mb: 2,
                    position: "relative",
                    zIndex: 1,
                    textAlign: { xs: "center", md: "right" },
                  }}
                >
                  برای مشاوره و هماهنگی در شبکه‌های اجتماعی پیام دهید
                </Typography>

                {/* دکمه‌های شبکه‌های اجتماعی */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {coach.socialMedia.map((social, idx) => {
                    const platformData =
                      SocialIconMap[social?.platform?.toLowerCase()] ||
                      SocialIconMap.website;
                    const IconComponent = platformData.icon;

                    return (
                      <IconButton
                        key={idx}
                        size="medium"
                        onClick={() => window.open(social?.url, "_blank")}
                        aria-label={social?.platform}
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.04)",
                          color: platformData.color, // رنگ آیکون در حالت عادی
                          border: "1px solid rgba(255, 255, 255, 0.05)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            bgcolor: platformData.color, // تغییر بک‌گراند به رنگ برند در هاور
                            color: "#fff", // سفید شدن آیکون در هاور
                            transform: "translateY(-4px)",
                            boxShadow: `0 8px 20px ${platformData.color}50`,
                            border: `1px solid ${platformData.color}`,
                          },
                        }}
                      >
                        <IconComponent sx={{ fontSize: 20 }} />
                      </IconButton>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
}
